/**
 * Applying a template to the OPEN space (owner, 2026-08-29, annotated
 * screenshot): "if I open an existing space, I should be able to 'add
 * template' and it populates to the currently open space v. attempting to
 * create a new space with the selected template."
 *
 * Ruling on the destructive case (owner, same session): "ask for
 * confirmation before replacing" — and the confirmation must say WHAT will
 * be lost, not a generic are-you-sure (SSH-11: a designer must be able to
 * tell whether their work survived). An EMPTY space must NOT prompt: a
 * confirmation on every application trains click-through and protects
 * nothing on the day it matters.
 *
 * Pure decisions only — app.js supplies the glue (the confirm dialog, the
 * store writes, the reload). Creating a NEW space from a template is
 * unchanged and lives in the identity dialog's own template picker.
 */

/**
 * What the open sketch would lose. Counts every kind of drawn content —
 * walls, placed sections, obstructions, ceiling openings, partitions, and
 * Basic-mode staged cards — because each is real work a replace destroys.
 *
 * @param {object} state the live sketch-model state (or a serialized doc)
 * @returns {{walls:number, sections:number, obstructions:number,
 *            openings:number, partitions:number, cards:number, empty:boolean}}
 */
export function sketchContentSummary(state = {}) {
  const n = (v) => (Array.isArray(v) ? v.length : 0);
  const summary = {
    walls: n(state.walls),
    sections: n(state.sections),
    obstructions: n(state.obstructions),
    openings: n(state.ceilingOpenings),
    partitions: n(state.partitions),
    cards: n(state.basic?.cards),
  };
  summary.empty = Object.values(summary).every((count) => count === 0);
  return summary;
}

/**
 * The decision for the library's "use template" control.
 *
 * @param {{spaceOpen:boolean, spaceName?:string, summary?:object}} args
 *   `spaceOpen` is false when no real space is open (fresh boot, or the open
 *   document is itself a template) — then the existing create-a-new-space
 *   flow runs unchanged.
 * @returns {{mode:'create'} | {mode:'apply'} | {mode:'confirm', message:string}}
 */
export function templateUseDecision({ spaceOpen, spaceName = '', summary } = {}) {
  if (!spaceOpen) return { mode: 'create' };
  const s = summary ?? sketchContentSummary({});
  if (s.empty) return { mode: 'apply' }; // nothing to lose — no prompt to train past
  return { mode: 'confirm', ...replaceConfirmation(spaceName, s) };
}

/**
 * The confirmation, naming the actual loss (SSH-11). Only the non-zero
 * kinds are listed, so it reads as an inventory of THIS space rather than
 * boilerplate. Returns {title, body} shaped for the HOUSE dialog
 * (`confirmAction`) — never the browser-native confirm, which some tablet
 * contexts suppress into a silent "no" (owner staging report, 2026-08-29:
 * "the outline of Use turns green but nothing happens"; confirm-dialog.js
 * had recorded that exact hazard since 2026-07-29).
 */
export function replaceConfirmation(spaceName, summary) {
  const parts = [];
  const add = (count, singular, plural = `${singular}s`) => {
    if (count > 0) parts.push(`${count} ${count === 1 ? singular : plural}`);
  };
  add(summary.walls, 'wall');
  add(summary.sections, 'section');
  add(summary.obstructions, 'obstruction');
  add(summary.openings, 'ceiling opening');
  add(summary.partitions, 'partition');
  add(summary.cards, 'staged card');
  const inventory = parts.length ? parts.join(', ') : 'its contents';
  const name = String(spaceName ?? '').trim() || 'this space';
  return {
    title: `Replace the contents of ${name}?`,
    body: `Its ${inventory} will be removed and the template becomes the new contents. Cancel keeps the space as it is.`,
  };
}

/**
 * Re-identify a freshly instantiated template copy as the OPEN space: same
 * id (so it is the same room row — the next push replaces the walls
 * wholesale, ADR 0022), same Client/Opportunity/Space/Version, same
 * preferred flag. The copy stops being a template; its content (walls,
 * sections, everything else) is untouched.
 *
 * @param {object} copy a doc from instantiateBaseRoomTemplate/duplicateSketchDoc
 * @param {{id:string, client:string, opportunity:string, space:string,
 *          version?:string, isPreferred?:boolean}} meta the open space's identity
 * @returns {object} the same doc, retargeted
 */
export function retargetTemplateCopy(copy, meta) {
  if (!copy || typeof copy !== 'object' || !meta?.id) return null;
  return Object.assign(copy, {
    id: meta.id,
    client: meta.client ?? '',
    opportunity: meta.opportunity ?? '',
    space: meta.space ?? '',
    version: meta.version || 'Version 1',
    isPreferred: meta.isPreferred === true,
    isTemplate: false,
    templateName: '',
  });
}
